import type { ConnectorDraft } from '../../../types/os';
import { connectorsApi } from '../../../services/connectorsApi';
import { PROVIDER_BY_TOOL_PREFIX } from './connectorToolMap';

function safeParseJSON(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value !== 'string') return null;
  const t = value.trim();
  if (!(t.startsWith('{') && t.endsWith('}'))) return null;
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}

/**
 * Scan assistant messages for propose_* tool calls whose result has a draft_id.
 * For each, check backend aliveness and attach a synthesized `connectorDraft`
 * to the matching message. Returns a new array with mutations applied.
 *
 * Failures (404 or network error) are silently swallowed — the stale tool-call
 * bubble remains visible, which is the correct UX for expired drafts.
 */
export async function rehydrateConnectorDrafts<
  M extends {
    role?: string;
    connectorDraft?: ConnectorDraft;
    connectorDrafts?: ConnectorDraft[];
    tool_calls?: Array<{
      tool_name?: string;
      name?: string;
      tool_call_id?: string;
      id?: string;
      result?: unknown;
    }>;
    toolCall?: { tool_name?: string; tool_call_id?: string; result?: unknown };
  },
>(messages: M[], workspaceId: string): Promise<M[]> {
  if (!workspaceId || !messages?.length) return messages;

  type Candidate = {
    messageIndex: number;
    draftId: string;
    toolCallId: string;
    toolName: string;
    parsed: Record<string, unknown>;
  };

  const candidates: Candidate[] = [];

  messages.forEach((m, i) => {
    // useSessionLoader re-maps agno's `assistant` role → `agent` for ChatMessage.
    // Live streams also tag agent messages with role `agent`. Accept both so
    // the same function works for fresh-stream and on-reload paths.
    const isAgentTurn = m.role === 'agent' || m.role === 'assistant';
    // Skip if already hydrated via the new array form
    if (!isAgentTurn || (m.connectorDraft && !m.connectorDrafts) || m.connectorDrafts?.length) return;

    const tcs = m.tool_calls ?? (m.toolCall ? [m.toolCall] : []);
    for (const tc of tcs as any[]) {
      const toolName: string = tc?.tool_name || tc?.name || '';
      if (!toolName.startsWith('propose_')) continue;
      if (!PROVIDER_BY_TOOL_PREFIX[toolName]) continue;

      const parsed = safeParseJSON(tc?.result);
      if (!parsed) continue;

      const draftId = parsed.draft_id as string | undefined;
      if (!draftId) continue;

      candidates.push({
        messageIndex: i,
        draftId,
        toolCallId: tc?.tool_call_id || tc?.id || '',
        toolName,
        parsed,
      });
      // continue — collect ALL propose_* tool calls in the message (multi-draft support)
    }
  });

  // eslint-disable-next-line no-console
  console.log('[rehydrateConnectorDrafts] scanned', messages.length,
              'messages; found', candidates.length, 'candidate(s):',
              candidates.map((c) => ({ idx: c.messageIndex, tool: c.toolName, draft: c.draftId })));

  if (!candidates.length) return messages;

  const alive = await Promise.allSettled(
    candidates.map((c) => connectorsApi.getDraft(workspaceId, c.draftId)),
  );

  const next = [...messages];
  // Group resolved drafts by message index so we build the array in one shot
  const draftsByMessage = new Map<number, ConnectorDraft[]>();

  candidates.forEach((c, i) => {
    const r = alive[i];
    if (r.status !== 'fulfilled') {
      // eslint-disable-next-line no-console
      console.log('[rehydrateConnectorDrafts] draft', c.draftId,
                  'not alive — leaving stale tool-call bubble. Reason:',
                  (r as any).reason?.response?.status || (r as any).reason?.message);
      return;
    }

    const mapEntry = PROVIDER_BY_TOOL_PREFIX[c.toolName];
    const preview = (c.parsed as any).preview ?? {};

    const draft: ConnectorDraft = {
      draft_id: c.draftId,
      provider: mapEntry.provider,
      action: mapEntry.action,
      tool_call_id: c.toolCallId,
      preview,
      status: (r.value as any)?.data?.approved ? 'sent' : 'pending',
    };

    // eslint-disable-next-line no-console
    console.log('[rehydrateConnectorDrafts] attaching card to msg',
                c.messageIndex, 'provider=', draft.provider,
                'status=', draft.status);

    const existing = draftsByMessage.get(c.messageIndex) ?? [];
    draftsByMessage.set(c.messageIndex, [...existing, draft]);
  });

  draftsByMessage.forEach((drafts, msgIdx) => {
    next[msgIdx] = {
      ...next[msgIdx],
      connectorDrafts: drafts,
      connectorDraft: undefined, // migrate away from singleton
    };
  });

  return next;
}
